<template>
	<div class="reactions-wrapper">
		<transition-group
			name="reaction"
			tag="div"
			class="reactions"
			:class="{ 'has-reactions': groupedReactions.length }"
		>
			<div
				class="reaction-item"
				v-for="group in groupedReactions"
				:key="group.emoji_base"
				:class="{
					me: group.me,
					'two-digits': group.count > 9,
					'three-digits': group.count > 99,
					'is-firefox': isFirefox,
				}"
			>
				<Tippy
					:content="group.tip"
					class="tip reaction-emoji"
					theme="bluewhite"
					arrow
					safe
					:options="{ allowHTML: true }"
				>
					<a
						@click.prevent="handleReactionClick(group)"
						:aria-label="group.emoji_base"
					>
						<div class="emoji-variations">
							<span
								v-for="variation in group.variations.slice(0, 10)"
								:key="variation.emoji"
								class="variation-emoji"
								>{{ variation.emoji }}</span
							>
							<span
								v-if="group.variations.length > 10"
								class="has-more-variations"
								>...</span
							>
						</div>
						<span class="count" v-text="group.count" />
					</a>
				</Tippy>
			</div>
		</transition-group>
		<ReactionsModal
			v-if="selectedReactionId"
			:message="message"
			:defaultTab="selectedReactionId"
			@cancel="selectedReactionId = null"
		/>
	</div>
</template>

<script>
import { Tippy } from '@/components/common'
import ReactionsModal from './ReactionsModal'
import {
	getUser,
	getMyUserId,
	sortUsersByFullname,
	getChat,
	isFirefox,
} from '@/store'
import { sanitizeHTML } from '@/helpers/utils'
import { getPreferredSkinToneForEmoji } from '@/helpers/emojis'
import { hideAll } from 'tippy.js'

const MAX_NAMES = 3

export default {
	name: 'Reactions',
	components: { Tippy, ReactionsModal },
	props: ['message'],
	data() {
		return { selectedReactionId: null, isFirefox }
	},
	emits: ['react'],
	computed: {
		groupedReactions() {
			if (!this.message.reactions) return []
			const groupedMap = new Map()

			for (const data of Object.values(this.message.reactions)) {
				const base = data.emoji_base
				if (!groupedMap.has(base)) {
					groupedMap.set(base, {
						emoji_base: base,
						count: 0,
						variations: [],
						allUsers: new Set(),
					})
				}

				const group = groupedMap.get(base)
				group.count += data.count
				data.users.forEach((userId) => group.allUsers.add(userId))

				group.variations.push({
					emoji: data.emoji,
					me: data.me,
					users: data.users,
				})
			}

			// After all variations are added, build the tooltip for each group
			for (const group of groupedMap.values()) {
				group.allUsers = Array.from(group.allUsers)
				group.me = group.variations.some((v) => v.me)

				const div = document.createElement('div')
				div.className = 'reactions-tip'
				div.innerHTML = this.getSafeHTML(group)

				if (div.innerHTML.includes('<a>')) {
					div.className += ' clickable'
					div.addEventListener('click', () => {
						hideAll({ duration: 0 })
						this.selectedReactionId = group.emoji_base
					})
				}

				group.tip = div
			}

			return Array.from(groupedMap.values())
		},
	},
	methods: {
		getSafeHTML(r) {
			return sanitizeHTML(this.getTipText(r))
		},

		getTipText(r) {
			const chat = getChat(this.message.bubble_id)
			if (chat?.async_reactions_enabled) {
				return this.getCountOnlyText(r.me, r.count)
			}
			// in groups, show count of total users
			const myId = getMyUserId()
			const users = r.allUsers
				.map(getUser)
				.filter((u) => u && u.id !== myId)
				.sort(sortUsersByFullname)
				.slice(0, MAX_NAMES)
				.map((u) => u.fullname)
			return this.getStandardText(r.me, r.count, users)
		},

		getCountOnlyText(me, Count) {
			if (me) {
				return Count === 1
					? this.$t('chat_reactions_tip_me')
					: this.$t(
							'chat_reactions_tip_me_other',
							{
								Count: Count - 1,
							},
							Count - 1
					  )
			}
			return this.$t('chat_reactions_tip_people_count', { Count }, Count)
		},

		getStandardText(me, count, users) {
			switch (count) {
				case 1:
					// if i'm the only one #tiggerstyle
					if (me) return this.$t('chat_reactions_tip_me')
					// if there's only 1 other
					return this.$t('chat_reactions_tip_dm_other', { Name: users[0] })
				case 2:
					// if its me and one other
					if (me) return this.$t('chat_reactions_tip_me_dm', { Name: users[0] })
					// if there are only two others
					return this.$t('chat_reactions_tip_multiple', {
						Users: users[0],
						Other: users[1],
					})
				case 3:
					// if its me and only two others
					if (me)
						return this.$t('chat_reactions_tip_me_multiple', {
							Users: users.join(',<br>'),
						})
					// if there are only 3 others
					return this.$t('chat_reactions_tip_multiple', {
						Users: users.slice(0, 2).join(',<br>'),
						Other: users[2],
					})
				case 4:
					// if its me and 3 others
					if (me)
						return this.$t('chat_reactions_tip_me_multiple', {
							Users: users.join(',<br>'),
						})
					// if its only 4 others (behaves same as 4+ others)
					return this.$t(
						'chat_reactions_tip_multiple_more',
						{
							Users: users.join(',<br>'),
							Count: 1,
						},
						1
					)
				default:
					// if its me and 4+ others
					if (me)
						return this.$t(
							'chat_reactions_tip_me_multiple_more',
							{
								Users: users.join(',<br>'),
								Count: count - 4,
							},
							count - 4
						)
					// if there are 4+ others
					return this.$t(
						'chat_reactions_tip_multiple_more',
						{
							// Other: users.pop(),
							Users: users.join(',<br>'),
							Count: count - 3,
						},
						count - 3
					)
			}
		},
		handleReactionClick(group) {
			const myReaction = group.variations.find((v) => v.me)
			if (myReaction) {
				this.$emit('react', myReaction.emoji)
			} else {
				const emoji =
					getPreferredSkinToneForEmoji(group.emoji_base)?.emoji ||
					group.emoji_base
				this.$emit('react', emoji)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$width: 52px;

.reactions {
	position: relative;
	z-index: 2;
	margin-left: 52px;
	display: flex;
	align-items: center;
	height: 0;
	padding-top: 0;
	overflow: hidden;
	transition: all 0.3s ease-in-out 0.1s;
	transition-property: height, padding-top;
	display: flex;
	flex-wrap: wrap;

	&.has-reactions {
		height: auto;
		// padding-top: 5px;
	}
}
.reaction-item {
	white-space: nowrap;
	border: 1px solid var(--neutral-200);
	border-radius: 30px;
	padding: 1px 8px 0 8px;
	margin: 5px 6px 0 0;
	background-color: var(--neutral-0);
	&.me {
		border-color: var(--blue-300);
		.count {
			color: var(--blue-300);
		}
	}
	.count {
		font-size: 12px;
		color: var(--neutral-400);
		height: 14px;
	}
	.tip {
		font-size: 20px;
	}
	&:hover {
		border-color: var(--blue-300);
	}
	a {
		user-select: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		text-decoration: none;
		gap: 6px;
		&:focus {
			outline: none;
		}
	}

	.tip-hidden {
		display: none;
	}

	.variation-emoji:not(:last-child) {
		margin-right: 6px;
	}

	.has-more-variations {
		font-size: 12px;
		color: var(--neutral-800);
		font-family: 'Avenir-Heavy';
	}
	&.is-firefox {
		padding-top: 2px;
	}
}

@keyframes bounce {
	0% {
		width: 0;
		opacity: 0.2;
	}
	20% {
		transform: scale(0);
	}
	50% {
		opacity: 1;
	}
	75% {
		width: $width;
	}
	100% {
		transform: scale(1);
	}
}
.reaction-enter-active {
	animation: bounce 0.3s cubic-bezier(1, 0, 0.32, 1.5);
}
.reaction-leave-active {
	animation: bounce 0.3s linear reverse;
}
</style>

<style lang="scss">
.tippy-box {
	.reactions-tip {
		&.clickable {
			cursor: pointer;
		}
		> a {
			color: white;
			text-decoration: underline;
		}
	}
}
</style>
